import {
  EntityLocationDTO,
  MultiTenantLocation,
  salesCenterContactLocationIds,
} from '@user/store/map/typings'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  clearEntityDetails,
  clearSelectedServiceLocation,
  fetchEntityDetailAndProducts,
  fetchMultiTenantEntityDetail,
  selectContactLocations,
  selectMapLinkClickedContactLocation,
  selectSearchFoundContactLocation,
  setMapLinkClickedContactLocation,
  setOverrideFilters,
  setSearchFoundContactLocation,
  setSelectedServiceLocationId,
  setSelectedServiceLocations,
  setPestPacLocationDetail,
  setPestPacLocationDetailStatus,
  setPestPacLocationPin,
  clearPestPacLocationDetail,
  withPestPacKnockStatus,
} from '@user/store/map'
import { getVariantFromEntityType } from '@user/components/HereMap/utils/createMarker'
import { trySwapPestPacDrawer } from '@user/components/HereMap/utils/pestPacDrawerSwap'
import { findPestPacPinForOpenAddressBubble } from '@user/components/HereMap/utils/pestPacBubbleSwap'
import { findRecordPinForOpenKnockStatusBubble } from '@user/components/HereMap/utils/knockStatusBubbleSwap'
import { useSelector } from 'react-redux'
import { ENTITY_TYPES, useAppDispatch } from '@sales-center/common'
import {
  MapPinTypeVariant,
  MapPinVariant,
} from '@user/components/HereMap/MapPin/types'
import {
  AddressLocation,
  HereMapClickEvent,
} from '@user/components/HereMap/HereMapNew/hooks/useHereMapInstance'
import { getAllKnockStatuses } from '@user/store/knockStatus/api'
import { getRecordsAtLocation } from '@user/store/map/api'
import { isSameAddress, isSameCoordinate } from '@user/utils/addressMatcher'
import { isSameLocationIdentity } from '@user/utils/locationMatching'
import { resolveExistingRecordPin } from '@user/features/MapView/RecordsAtLocation/recordsAtLocationPin'
import { HistoryItem } from '@user/features/MapView/KnockStatusModal/hooks'
import { PendingCustomReason } from '@user/components/HereMap/hooks'
import { KnockStatusLocationBaseInput } from '@user/store/knockStatus'
import { fetchPestPacLocationDetail } from '@user/store/map/api'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferencesBatch,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { toast } from '@sales-center/components'
import { selectCurrentUser } from '@user/store/authorization'
import { useFilterChipData } from '@user/components/HereMap/hooks/useFilterChipData'
import {
  FILTERS_AUTO_UPDATED_LEADS,
  computeFilterUpdateForNewRecord,
} from '@user/features/MapView/RecordsHiddenByFilters/filterUpdateForNewRecord'

const HOVER_OPEN_DELAY_MS = 2000
const PIN_CLICK_SUPPRESS_MS = 400

export type MapClickAction =
  | { type: 'address-match'; matchedLocation: EntityLocationDTO }
  | { type: 'toggle-close' }
  | { type: 'clear-and-refetch' }

// Pure decision logic for handleMapClick (SH-1269) — exported so it can be unit-tested
// directly instead of via a duplicated "mirror" that could drift from the real behavior.
export function resolveMapClickAction(
  clickedPin: EntityLocationDTO | null,
  locationData: HereMapClickEvent,
  contactLocations: EntityLocationDTO[],
): MapClickAction {
  const clickedPinAddress: AddressLocation | undefined = clickedPin
    ? (clickedPin.address ?? {
        street1: clickedPin.street1,
        city: clickedPin.city,
        stateAbbreviation: clickedPin.stateAbbreviation,
        postalCode: clickedPin.postalCode,
      })
    : undefined

  const clickedPinMatchesLocation =
    !!clickedPin &&
    (clickedPinAddress?.street1 && locationData.address?.street1
      ? isSameAddress(clickedPinAddress, locationData.address)
      : isSameCoordinate(
          clickedPin.latitude,
          clickedPin.longitude,
          locationData.latitude,
          locationData.longitude,
        ))

  const matchedLocation = contactLocations.find((loc) =>
    isSameAddress(loc.address, locationData.address),
  )

  if (matchedLocation) return { type: 'address-match', matchedLocation }
  if (clickedPinMatchesLocation) return { type: 'toggle-close' }
  return { type: 'clear-and-refetch' }
}

// Pure check for whether a pin/location has a backing Lead/Opportunity/
// DraftLead entity (SH-1179) — exported so it can be unit-tested directly,
// mirroring resolveMapClickAction above.
export function hasResolvableEntity(location: EntityLocationDTO): boolean {
  return Boolean(location.entityId)
}

type Props = {
  setHoveredPin: (pin: EntityLocationDTO | null) => void
  clickedPin: EntityLocationDTO | null
  setClickedPin: (pin: EntityLocationDTO | null) => void
  tooltipHovered: boolean
  setDrawerOpen: (value: boolean) => void
  setMultiTenantDrawerOpen: (value: boolean) => void
  setSearchDrawerOpen: (value: boolean) => void
  setFoundLocationFromSearch: (value: boolean) => void
  setFilterDrawerOpen: (value: boolean) => void
  setTooltipHovered: (value: boolean) => void
  setAddLeadModalOpen: (value: boolean) => void
  setAddressDetailsForAddLead: (data: HereMapClickEvent | null) => void
  addressDetailsForAddLead: HereMapClickEvent | null
  setAddressDetailsForAddLeadTemp: (data: HereMapClickEvent | null) => void
  addressDetailsForAddLeadTemp: HereMapClickEvent | null
  drawerOpen: boolean
  multiTenantDrawerOpen: boolean
  searchDrawerOpen: boolean
  filterDrawerOpen: boolean
  open: boolean
  onCloseProp: () => void
  setOpenKnockStatusModal: (value: boolean) => void
  setShowHistory: (value: boolean) => void
  setAddressDetailsForKnockStatus: (data: EntityLocationDTO | null) => void
  setAddressDetailsForKnockStatusTemp: (data: HereMapClickEvent | null) => void
  convertHereCoordsToEntityLocation: (
    location: HereMapClickEvent,
  ) => EntityLocationDTO
  convertEntityLocationToHereEvent: (
    location: EntityLocationDTO,
  ) => HereMapClickEvent
  setPendingCustomReason: (data: PendingCustomReason | null) => void
  onReviewPendingLead?: (draftId: string) => void
}

export const useHandler = (props: Props) => {
  const {
    tooltipHovered,
    clickedPin,
    setHoveredPin,
    setClickedPin,
    setDrawerOpen,
    setMultiTenantDrawerOpen,
    drawerOpen,
    multiTenantDrawerOpen,
    setSearchDrawerOpen,
    setFoundLocationFromSearch,
    setFilterDrawerOpen,
    filterDrawerOpen,
    setTooltipHovered,
    setAddressDetailsForAddLead,
    addressDetailsForAddLead,
    setAddressDetailsForAddLeadTemp,
    addressDetailsForAddLeadTemp,
    searchDrawerOpen,
    open,
    setAddLeadModalOpen,
    onCloseProp,
    setOpenKnockStatusModal,
    setShowHistory,
    setAddressDetailsForKnockStatus,
    convertHereCoordsToEntityLocation,
    convertEntityLocationToHereEvent,
    setPendingCustomReason,
    onReviewPendingLead,
  } = props
  const dispatch = useAppDispatch()
  const foundLocationFromSearch = useSelector(selectSearchFoundContactLocation)
  const contactLocations = useSelector(selectContactLocations)
  const mapLinkClickedContactLocation = useSelector(
    selectMapLinkClickedContactLocation,
  )
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)
  const currentUser = useSelector(selectCurrentUser)
  const { selectedTypes, selectedKnockStatusIds, myRecords } =
    useFilterChipData()

  const selectedTypesSig = selectedTypes.join('_')
  const selectedKnockStatusIdsSig = selectedKnockStatusIds.join('_')
  const activeMapFilters = useMemo(
    () => ({ selectedTypes, selectedKnockStatusIds, myRecords }),
    [selectedTypesSig, selectedKnockStatusIdsSig, myRecords],
  )

  // Whether an entity type is currently visible in the map filter (SH-1403). The active-pin
  // overlay renders clickedPin WITHOUT applying the entity-type filter, so a just-created record
  // whose type is filtered out must not be set as the active pin. Only lead/opportunity/draftlead
  // are governed by this filter; other types (e.g. PestPac) are always allowed.
  const isEntityTypeVisibleInFilter = useCallback(
    (entityType: string | null | undefined) => {
      const key = entityType?.toLowerCase()
      if (
        key !== MapPinTypeVariant.Lead &&
        key !== MapPinTypeVariant.Opportunity &&
        key !== MapPinTypeVariant.DraftLead
      )
        return true
      const pref = userGeneralPreferences?.find(
        (row) =>
          row?.type ===
          IUserPreferenceGeneralListItemType.MapViewFilteredEntityTypes,
      )
      return (pref?.value ?? 'draftlead_lead_opportunity')
        .split('_')
        .filter(Boolean)
        .includes(key)
    },
    [userGeneralPreferences],
  )
  const clickedLinkPinIdRef = useRef<string | null>(null)
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const hoverOpenTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const tooltipHoveredRef = useRef(false)
  const clickedPinRef = useRef<EntityLocationDTO | null>(null)
  const pinClickAtRef = useRef(0)
  const drawerOpenRef = useRef<boolean>(false)
  const contactLocationsRef = useRef(contactLocations)
  const hoveredKnockStatusRef = useRef(false)

  useEffect(() => {
    contactLocationsRef.current = contactLocations
  }, [contactLocations])

  useEffect(() => {
    tooltipHoveredRef.current = tooltipHovered
  }, [tooltipHovered])

  useEffect(() => {
    clickedPinRef.current = clickedPin
  }, [clickedPin])

  useEffect(() => {
    drawerOpenRef.current = drawerOpen || multiTenantDrawerOpen
  }, [drawerOpen, multiTenantDrawerOpen])

  useEffect(() => {
    return () => {
      if (hoverOpenTimeoutRef.current) {
        clearTimeout(hoverOpenTimeoutRef.current)
      }
    }
  }, [])

  // When tiles refresh (e.g. after knock status save), sync the updated
  // location data into clickedPin so the pin badge updates immediately
  // while the detail drawer is still open.
  //
  // isSameLocationIdentity is shared with HereMapNew, which decides off the same
  // identity which bubble to render. The two must agree: matching a bare
  // KnockStatusLocationId here — without the entity-type check — silently
  // re-pointed clickedPin from a knock-status-only pin onto the Lead / PestPac
  // pin created at the same address, which HereMapNew still considered a
  // different pin, so both bubbles rendered at once (SH-1091).
  useEffect(() => {
    if (!clickedPin || contactLocations.length === 0) return
    const refreshed = contactLocations.find((loc) =>
      isSameLocationIdentity(loc, clickedPin),
    )
    if (refreshed && refreshed !== clickedPin) {
      setClickedPin(refreshed)
    }
  }, [clickedPin, contactLocations, setClickedPin])

  // A PestPac location added in PestPac itself for an address whose bubble is already open
  // arrives with the next tile refresh (e.g. the refetch useRefreshOnTabFocus runs when the
  // user comes back to the map). The address bubble is a click-time snapshot, so without this
  // it keeps showing the address card over the new PestPac pin until dismissed — promote it to
  // the PestPac preview bubble instead, exactly as a click on that pin would (SH-1713).
  useEffect(() => {
    // A drawer owns the selection while it's open: useRefreshOnTabFocus re-resolves the
    // drawer's own record, and trySwapPestPacDrawer handles PP pins clicked over it (SH-1424).
    if (drawerOpen || multiTenantDrawerOpen) return

    const pestPacPin = findPestPacPinForOpenAddressBubble(
      addressDetailsForAddLeadTemp,
      clickedPin,
      contactLocations,
      { filters: activeMapFilters, currentUserId: currentUser?.userId ?? null },
    )
    if (!pestPacPin) return

    setHoveredPin(null)
    setClickedPin(pestPacPin)
    // Both bubbles render off separate state and PP pins carry entityId 0, so the address
    // bubble must be dropped in the same update or it would sit on top of the PestPac one.
    setAddressDetailsForAddLeadTemp(null)
  }, [
    activeMapFilters,
    addressDetailsForAddLeadTemp,
    clickedPin,
    contactLocations,
    currentUser?.userId,
    drawerOpen,
    multiTenantDrawerOpen,
    setAddressDetailsForAddLeadTemp,
    setClickedPin,
    setHoveredPin,
  ])

  // The same defect one step further along: a Lead / Opportunity created for the
  // address of a pinned-open knock-status-only bubble — in PestPac or in a second
  // Sales Center tab — arrives with the next tile refresh and renders as its own
  // pin, while the stale knock-status snapshot keeps sitting on top of it (SH-1091).
  // The effect above only handles the bubble with nothing clicked; this one handles
  // the knock-status badge the user clicked open. It runs second, so when both
  // could apply the PestPac swap wins and this one finds the bubble already gone.
  useEffect(() => {
    // A drawer owns the selection while it's open: useRefreshOnTabFocus
    // re-resolves the drawer's own record after the same refresh.
    if (drawerOpen || multiTenantDrawerOpen) return

    const recordPin = findRecordPinForOpenKnockStatusBubble(
      addressDetailsForAddLeadTemp,
      clickedPin,
      contactLocations,
      hoveredKnockStatusRef.current,
    )
    if (!recordPin) return

    setHoveredPin(null)
    setClickedPin(recordPin)
    // The two bubbles render off separate state, and the knock-status bubble's
    // gate (entityId 0) is still satisfied by a PestPac pin, so the stale
    // snapshot must be dropped in the same update or it stays on top.
    setAddressDetailsForAddLeadTemp(null)
  }, [
    addressDetailsForAddLeadTemp,
    clickedPin,
    contactLocations,
    drawerOpen,
    multiTenantDrawerOpen,
    setAddressDetailsForAddLeadTemp,
    setClickedPin,
    setHoveredPin,
  ])

  const onClose = useCallback(() => {
    setClickedPin(null)
    setHoveredPin(null)
    setTooltipHovered(false)
    clickedPinRef.current = null
  }, [setClickedPin, setHoveredPin, setTooltipHovered])

  const openKnockStatusModalByMode = useCallback(
    (knockStatusLocation: EntityLocationDTO, showHistoryModal: boolean) => {
      setDrawerOpen(false)
      setMultiTenantDrawerOpen(false)
      setSearchDrawerOpen(false)
      setFoundLocationFromSearch(false)
      setFilterDrawerOpen(false)
      dispatch(getAllKnockStatuses())
      setAddressDetailsForKnockStatus(knockStatusLocation)
      setOpenKnockStatusModal(true)
      setShowHistory(showHistoryModal)
    },
    [
      setDrawerOpen,
      setMultiTenantDrawerOpen,
      setSearchDrawerOpen,
      setFoundLocationFromSearch,
      setFilterDrawerOpen,
      dispatch,
      setAddressDetailsForKnockStatus,
      setOpenKnockStatusModal,
      setShowHistory,
    ],
  )

  const knockStatusFromMapHandler = useCallback(
    (knockStatusLocation: EntityLocationDTO) => {
      openKnockStatusModalByMode(knockStatusLocation, false)
    },
    [openKnockStatusModalByMode],
  )

  const knockStatusHistoryFromMapHandler = useCallback(
    (knockStatusLocation: EntityLocationDTO) => {
      openKnockStatusModalByMode(knockStatusLocation, true)
    },
    [openKnockStatusModalByMode],
  )

  const nameClickHandler = useCallback(
    (entityId: number, variant: string) => {
      if (
        (variant.toLowerCase() === 'pendinglead' ||
          variant.toLowerCase() === 'draftlead') &&
        onReviewPendingLead
      ) {
        onReviewPendingLead(entityId.toString())
        return
      }

      let path = ''
      switch (variant.toLowerCase()) {
        case 'lead':
          path = `/#/leads/${entityId}`
          break
        case 'opportunity':
          path = `/#/opportunities/${entityId}`
          break
        case 'pendinglead':
        case 'draftlead':
          path = `/#/leads/addLead?draftId=${entityId}`
          break
        default:
          break
      }

      if (path) window.open(path, '_blank', 'noopener,noreferrer')
    },
    [onReviewPendingLead],
  )

  const viewClickHandler = useCallback(
    (
      contactLocation: EntityLocationDTO,
      shouldFetchDetails?: boolean,
      options?: { keepMultiTenantDrawerOpen?: boolean },
    ) => {
      const keepMultiTenantDrawerOpen = !!options?.keepMultiTenantDrawerOpen

      if (
        contactLocation.entityType?.toLowerCase() === MapPinTypeVariant.PestPac
      ) {
        setDrawerOpen(true)
        if (!keepMultiTenantDrawerOpen) setMultiTenantDrawerOpen(false)
        setSearchDrawerOpen(false)
        setFoundLocationFromSearch(false)
        setFilterDrawerOpen(false)

        dispatch(setPestPacLocationPin(contactLocation))

        const locationId = contactLocation.pestPacLocationId

        if (locationId) {
          dispatch(setPestPacLocationDetailStatus('loading'))
          fetchPestPacLocationDetail(locationId)
            .then((data) => {
              dispatch(setPestPacLocationDetail(data))
              dispatch(setPestPacLocationDetailStatus('success'))
              // Re-derive the pin's knock-status fields from the fresh detail so "View History"
              // works on reopen, not only while the panel stayed open (SH-661).
              dispatch(
                setPestPacLocationPin(
                  withPestPacKnockStatus(contactLocation, data),
                ),
              )
            })
            .catch((error) => {
              console.error(
                '[viewClickHandler] Failed to fetch PestPac location detail:',
                error,
              )
              dispatch(setPestPacLocationDetailStatus('error'))
            })
        } else {
          console.error('[viewClickHandler] No locationId found')
        }
        return
      }

      dispatch(clearPestPacLocationDetail())
      setDrawerOpen(true)
      if (!keepMultiTenantDrawerOpen) setMultiTenantDrawerOpen(false)
      setSearchDrawerOpen(false)
      setFoundLocationFromSearch(false)
      setFilterDrawerOpen(false)
      const id = contactLocation.entityId
      const serviceLocationId = contactLocation.serviceLocationId
      const variant = getVariantFromEntityType(contactLocation.entityType)
      if (shouldFetchDetails) {
        dispatch(
          fetchEntityDetailAndProducts([variant, id, serviceLocationId ?? 0]),
        )
      }
      dispatch(setSelectedServiceLocationId(serviceLocationId))
    },
    [
      setDrawerOpen,
      setMultiTenantDrawerOpen,
      setSearchDrawerOpen,
      setFoundLocationFromSearch,
      setFilterDrawerOpen,
      dispatch,
    ],
  )

  const viewMultiTenantClickHandler = useCallback(
    (explicitList: MultiTenantLocation[]) => {
      dispatch(clearPestPacLocationDetail())
      setDrawerOpen(false)
      setMultiTenantDrawerOpen(true)
      setSearchDrawerOpen(false)
      setFoundLocationFromSearch(false)
      setFilterDrawerOpen(false)
      dispatch(setSelectedServiceLocations(explicitList))
      dispatch(
        fetchMultiTenantEntityDetail(
          salesCenterContactLocationIds(explicitList),
        ),
      )
    },
    [
      dispatch,
      setDrawerOpen,
      setMultiTenantDrawerOpen,
      setSearchDrawerOpen,
      setFoundLocationFromSearch,
      setFilterDrawerOpen,
    ],
  )

  const pestPacAddLeadClickHandler = useCallback(
    (contactLocation: EntityLocationDTO) => {
      const mapped = convertEntityLocationToHereEvent(contactLocation)
      setAddressDetailsForAddLead(mapped)
      setAddressDetailsForAddLeadTemp(null)
      setAddLeadModalOpen(true)
    },
    [
      convertEntityLocationToHereEvent,
      setAddressDetailsForAddLead,
      setAddressDetailsForAddLeadTemp,
      setAddLeadModalOpen,
    ],
  )

  const onActionClick = useCallback(
    (action: string, contactLocation: EntityLocationDTO) => {
      setTooltipHovered(false)
      switch (action) {
        case 'nameClick':
          nameClickHandler(contactLocation.entityId, contactLocation.entityType)
          break
        case 'viewClick':
          viewClickHandler(contactLocation, false)
          break
        case 'multiTenantView':
          viewMultiTenantClickHandler(contactLocation.entities)
          break
        case 'knockStatusClickMapper':
          knockStatusFromMapHandler(contactLocation)
          break
        case 'knockStatusHistoryClickMapper':
          knockStatusHistoryFromMapHandler(contactLocation)
          break
        case 'pestPacAddLead':
          pestPacAddLeadClickHandler(contactLocation)
          break
        default:
          break
      }
    },
    [
      nameClickHandler,
      setTooltipHovered,
      viewClickHandler,
      viewMultiTenantClickHandler,
      knockStatusFromMapHandler,
      knockStatusHistoryFromMapHandler,
      pestPacAddLeadClickHandler,
    ],
  )

  const cancelHoverOpen = useCallback(() => {
    if (hoverOpenTimeoutRef.current) {
      clearTimeout(hoverOpenTimeoutRef.current)
      hoverOpenTimeoutRef.current = null
    }
  }, [])

  const markPinClicked = useCallback(() => {
    pinClickAtRef.current = performance.now()
    hoveredKnockStatusRef.current = false
  }, [])
  const isHoverPreview = useCallback(() => hoveredKnockStatusRef.current, [])

  const openHoveredPin = useCallback(
    (location: EntityLocationDTO) => {
      if (drawerOpenRef.current || clickedPinRef.current) {
        return
      }
      if (location.entityType.toLowerCase() === MapPinTypeVariant.KnockStatus) {
        hoveredKnockStatusRef.current = true
        setHoveredPin(null)
        setAddressDetailsForAddLeadTemp(
          convertEntityLocationToHereEvent(location),
        )
        return
      }
      setHoveredPin(location)
      setAddressDetailsForAddLeadTemp(null)
    },
    [
      convertEntityLocationToHereEvent,
      setAddressDetailsForAddLeadTemp,
      setHoveredPin,
    ],
  )

  // ----- Hover handler -----
  const onMarkerHover = useCallback(
    (location: EntityLocationDTO | null, immediate = false) => {
      dispatch(setMapLinkClickedContactLocation(null))
      if (drawerOpenRef.current) {
        return
      }

      if (clickedPinRef.current) {
        return
      }

      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
        hoverTimeoutRef.current = null
      }
      cancelHoverOpen()

      if (location === null) {
        hoverTimeoutRef.current = setTimeout(() => {
          if (
            !tooltipHoveredRef.current &&
            !clickedPinRef.current &&
            performance.now() - pinClickAtRef.current >= PIN_CLICK_SUPPRESS_MS
          ) {
            setHoveredPin(null)
            setAddressDetailsForAddLeadTemp(null)
          }
        }, 100)
        return
      }

      if (immediate) {
        openHoveredPin(location)
      } else {
        hoverOpenTimeoutRef.current = setTimeout(() => {
          openHoveredPin(location)
        }, HOVER_OPEN_DELAY_MS)
      }
    },
    [
      cancelHoverOpen,
      dispatch,
      openHoveredPin,
      setAddressDetailsForAddLeadTemp,
      setHoveredPin,
    ],
  )

  // ----- Click handler -----
  const onMarkerClick = useCallback(
    (markerData: EntityLocationDTO | null) => {
      if (!markerData) return
      cancelHoverOpen()
      markPinClicked()
      setTooltipHovered(true)
      if (markerData.entityId === 0) {
        // PP pins clicked while the drawer is open swap it to the new location
        // (SH-1424). Shared with handleMapLibreMarkerClick via trySwapPestPacDrawer.
        if (
          trySwapPestPacDrawer(markerData, drawerOpenRef.current, {
            setHoveredPin,
            setClickedPin,
            setAddressDetailsForAddLeadTemp,
            viewClickHandler,
          })
        ) {
          return
        }
        setHoveredPin(null)
        setClickedPin(markerData)
        setAddressDetailsForAddLeadTemp(
          convertEntityLocationToHereEvent(markerData),
        )
        return
      }
      setHoveredPin(null)
      setClickedPin(markerData)
      setAddressDetailsForAddLeadTemp(null)
      dispatch(setMapLinkClickedContactLocation(null))
      if (drawerOpenRef.current) {
        if (markerData.multitenant) {
          viewMultiTenantClickHandler(markerData.entities)
        } else {
          viewClickHandler(markerData, true)
        }
      }
    },
    [
      cancelHoverOpen,
      markPinClicked,
      setTooltipHovered,
      setHoveredPin,
      setClickedPin,
      setAddressDetailsForAddLeadTemp,
      dispatch,
      convertEntityLocationToHereEvent,
      viewMultiTenantClickHandler,
      viewClickHandler,
    ],
  )

  // ----- Map click handler -----
  const handleMapClick = useCallback(
    (locationData: HereMapClickEvent | undefined) => {
      if (!locationData) {
        return
      }

      if (performance.now() - pinClickAtRef.current < PIN_CLICK_SUPPRESS_MS) {
        return
      }

      setHoveredPin(null)

      const clickedPin = clickedPinRef.current
      const action = resolveMapClickAction(
        clickedPin,
        locationData,
        contactLocationsRef.current,
      )

      if (action.type === 'address-match') {
        onMarkerClick(action.matchedLocation)
        return
      }

      if (action.type === 'toggle-close') {
        setAddressDetailsForAddLeadTemp(null)
        return
      }

      // action.type === 'clear-and-refetch' (SH-1269) — a click on any different target while a
      // bubble is open must close it and fall through to the shared refetch flow below, rather
      // than re-opening the stale pin or leaving clickedPin stuck.
      setClickedPin(null)
      setAddressDetailsForAddLeadTemp(null)
      setDrawerOpen(false)
      setMultiTenantDrawerOpen(false)
      setSearchDrawerOpen(false)
      setFilterDrawerOpen(false)
      hoveredKnockStatusRef.current = false

      // SH-1143 — open the preview bubble only once the single coordinate lookup resolves, so it
      // appears already-correct (no default-then-update flicker).
      dispatch(
        getRecordsAtLocation({
          lat: locationData.latitude,
          lng: locationData.longitude,
        }),
      ).then((result) => {
        // If user clicked a pin while lookup was in-flight, discard result
        // to prevent re-opening address tooltip on top of entity bubble (SH-892)
        if (clickedPinRef.current) return
        const payload = getRecordsAtLocation.fulfilled.match(result)
          ? result.payload
          : null

        // This location already holds record(s): open THEIR preview bubble instead of the address
        // bubble, so a second record can't be added without seeing the existing one. Decided from
        // this lookup rather than the loaded pins, which are filtered client-side and may not carry
        // the address fields needed to match.
        const existingRecordPin = resolveExistingRecordPin(
          payload?.records,
          contactLocationsRef.current,
          {
            latitude: locationData.latitude,
            longitude: locationData.longitude,
            formattedAddress: locationData.label,
          },
          // SH-1762 — when every record here is hidden by the filters, fall through to the address
          // bubble so its hidden-by-filters banner shows instead of a record bubble.
          {
            filters: activeMapFilters,
            currentUserId: currentUser?.userId ?? null,
          },
        )
        if (existingRecordPin) {
          onMarkerClick(existingRecordPin)
          return
        }

        const knockStatus = payload?.knockStatus ?? null
        // SH-1307 -- the history list itself is the source of truth for whether
        // this location has any knock-status history; don't gate on the separate
        // hasHistory flag, since a location can have history with no active status
        // (e.g. the active one was deleted from Settings).
        const hasHistory = Boolean(knockStatus?.history?.length)
        // Attach the records so the bubble can flag filter-hidden ones without a second fetch.
        const base = { ...locationData, recordsAtLocation: payload?.records }
        setAddressDetailsForAddLeadTemp(
          hasHistory && knockStatus
            ? {
                ...base,
                knockStatusLocationId: knockStatus.knockStatusLocationId,
                knockStatusSettingName:
                  knockStatus.activeKnockStatusSettingName ?? undefined,
                knockStatusReason: knockStatus.activeCustomReason ?? undefined,
                knockStatusHistory: knockStatus.history,
                address: {
                  ...locationData.address,
                  knockStatusLocationId: knockStatus.knockStatusLocationId,
                },
              }
            : base,
        )
      })
    },
    [
      dispatch,
      onMarkerClick,
      setAddressDetailsForAddLeadTemp,
      setClickedPin,
      setDrawerOpen,
      setFilterDrawerOpen,
      setHoveredPin,
      setMultiTenantDrawerOpen,
      setSearchDrawerOpen,
      activeMapFilters,
      currentUser?.userId,
    ],
  )

  const closeDrawerAndRefresh = useCallback(() => {
    dispatch(setOverrideFilters(false))
  }, [dispatch])

  const resetMapState = useCallback(() => {
    setHoveredPin(null)
    setClickedPin(null)
    setFoundLocationFromSearch(false)
    dispatch(setMapLinkClickedContactLocation(null))
  }, [dispatch, setClickedPin, setFoundLocationFromSearch, setHoveredPin])

  const closeAndRefresh = useCallback(() => {
    resetMapState()
    closeDrawerAndRefresh()
  }, [closeDrawerAndRefresh, resetMapState])

  const closeAllDrawers = useCallback(() => {
    setDrawerOpen(false)
    setMultiTenantDrawerOpen(false)
    setClickedPin(null)
    setHoveredPin(null)
    closeAndRefresh()
  }, [
    closeAndRefresh,
    setClickedPin,
    setDrawerOpen,
    setHoveredPin,
    setMultiTenantDrawerOpen,
  ])

  useEffect(() => {
    if (foundLocationFromSearch) {
      // PestPac pins carry entityId 0 (like knock-status-only pins) but have a
      // real pestPacLocationId and their own detail panel, so they must open the
      // drawer, not fall into the KS-only close branch below (SH-1179).
      const isPestPac =
        foundLocationFromSearch.entityType?.toLowerCase() ===
        MapPinTypeVariant.PestPac

      if (foundLocationFromSearch.multitenant) {
        setMultiTenantDrawerOpen(true)
        setDrawerOpen(false)
        drawerOpenRef.current = true
      } else if (hasResolvableEntity(foundLocationFromSearch) || isPestPac) {
        // Entity-backed (Lead/Opportunity/DraftLead) or PestPac — both have a
        // detail panel. onMarkerClick repopulates it (viewClickHandler for
        // entity pins, trySwapPestPacDrawer for PestPac), both of which require
        // drawerOpenRef.current === true.
        setMultiTenantDrawerOpen(false)
        setDrawerOpen(true)
        drawerOpenRef.current = true
      } else {
        // A searched address that resolves to a knock-status-only location has
        // no backing Lead/Opportunity/DraftLead to load — opening the detail
        // drawer would leave it showing the skeleton or the previously selected
        // record. Keep it closed and clear stale details; onMarkerClick still
        // opens the preview bubble (SH-1179). Mirrors the map-link guard in
        // handleSetMapPin.
        dispatch(clearEntityDetails())
        setMultiTenantDrawerOpen(false)
        setDrawerOpen(false)
        drawerOpenRef.current = false
      }
      onMarkerClick(foundLocationFromSearch)
      dispatch(setSearchFoundContactLocation(null))
    }
  }, [
    dispatch,
    foundLocationFromSearch,
    onMarkerClick,
    setDrawerOpen,
    setMultiTenantDrawerOpen,
  ])

  const entityTypeToMapPinVariant = useCallback(
    (entityType: ENTITY_TYPES): MapPinVariant => {
      switch (entityType) {
        case ENTITY_TYPES.Lead:
          return 'lead'
        case ENTITY_TYPES.Opportunity:
          return 'opportunity'
        case ENTITY_TYPES.DraftLead:
          return 'pendinglead'
        default:
          return 'lead'
      }
    },
    [],
  )

  const handleAddressDetailsForAddLeadClose = useCallback(() => {
    setClickedPin(null)
    setTooltipHovered(false)
    setAddressDetailsForAddLeadTemp(null)
    setAddressDetailsForAddLead(null)
    // SH-2100: mirror the entity drawer close (closeDrawerAndRefresh). A search that revealed a
    // filtered-off knock-status badge leaves overrideFilters=true; without resetting it here, no
    // refetch runs and the revealed KS pin lingers in contactLocations after the preview closes.
    dispatch(setOverrideFilters(false))
  }, [
    dispatch,
    setAddressDetailsForAddLead,
    setAddressDetailsForAddLeadTemp,
    setTooltipHovered,
    setClickedPin,
  ])

  const handleLocationPreviewAddLead = useCallback(() => {
    setAddressDetailsForAddLead(addressDetailsForAddLeadTemp)
    setAddLeadModalOpen(true)
    setAddressDetailsForAddLeadTemp(null)
  }, [
    setAddLeadModalOpen,
    addressDetailsForAddLeadTemp,
    setAddressDetailsForAddLead,
    setAddressDetailsForAddLeadTemp,
  ])

  const handleLocationPreviewKnockStatus = useCallback(() => {
    if (!addressDetailsForAddLeadTemp) return

    const mapped = convertHereCoordsToEntityLocation(
      addressDetailsForAddLeadTemp,
    )

    onActionClick('knockStatusClickMapper', mapped)
  }, [
    addressDetailsForAddLeadTemp,
    onActionClick,
    convertHereCoordsToEntityLocation,
  ])

  const handleLocationPreviewKnockStatusHistory = useCallback(() => {
    if (!addressDetailsForAddLeadTemp) return

    const mapped = convertHereCoordsToEntityLocation(
      addressDetailsForAddLeadTemp,
    )

    onActionClick('knockStatusHistoryClickMapper', mapped)
  }, [
    addressDetailsForAddLeadTemp,
    onActionClick,
    convertHereCoordsToEntityLocation,
  ])

  const handleAddLeadModalClose = useCallback(() => {
    setAddLeadModalOpen(false)
    setAddressDetailsForAddLeadTemp(null)
    setAddressDetailsForAddLead(null)
  }, [
    setAddLeadModalOpen,
    setAddressDetailsForAddLead,
    setAddressDetailsForAddLeadTemp,
  ])

  const handleAddLeadSaveSuccess = useCallback(
    async (newPin: EntityLocationDTO) => {
      const activeKnockStatusSettingsId =
        addressDetailsForAddLead?.knockStatusSettingsId ??
        addressDetailsForAddLead?.knockStatusHistory?.find((h) => h.isActive)
          ?.knockStatusSettingsId ??
        null

      dispatch(clearPestPacLocationDetail())
      setAddLeadModalOpen(false)
      setAddressDetailsForAddLead(null)
      setAddressDetailsForAddLeadTemp(null)
      setDrawerOpen(true)
      setMultiTenantDrawerOpen(false)
      setHoveredPin(null)

      const { batch, changed } = computeFilterUpdateForNewRecord(
        {
          entityType: newPin.entityType,
          knockStatusSettingsId: activeKnockStatusSettingsId,
          ownerId: newPin.assigneeId ?? null,
        },
        activeMapFilters,
        currentUser?.userId ?? null,
      )

      let filtersUpdated = false
      if (changed) {
        const action = await dispatch(saveGeneralPreferencesBatch(batch))
        filtersUpdated = saveGeneralPreferencesBatch.fulfilled.match(action)
        if (filtersUpdated) {
          toast.success(FILTERS_AUTO_UPDATED_LEADS)
        }
      }

      setClickedPin(
        filtersUpdated || isEntityTypeVisibleInFilter(newPin.entityType)
          ? newPin
          : null,
      )

      const variant = getVariantFromEntityType(newPin.entityType)
      dispatch(
        fetchEntityDetailAndProducts([
          variant,
          newPin.entityId,
          newPin.serviceLocationId ?? 0,
        ]),
      )
      dispatch(setSelectedServiceLocationId(newPin.serviceLocationId))
    },
    [
      setAddLeadModalOpen,
      setAddressDetailsForAddLead,
      addressDetailsForAddLead,
      setAddressDetailsForAddLeadTemp,
      setClickedPin,
      setDrawerOpen,
      setHoveredPin,
      setMultiTenantDrawerOpen,
      dispatch,
      isEntityTypeVisibleInFilter,
      activeMapFilters,
      currentUser?.userId,
    ],
  )

  const handleDuplicateManageClick = useCallback(() => {
    setAddLeadModalOpen(false)
    setAddressDetailsForAddLead(null)
    setAddressDetailsForAddLeadTemp(null)
    setDrawerOpen(false)
    setMultiTenantDrawerOpen(false)
    onCloseProp()
  }, [
    onCloseProp,
    setAddLeadModalOpen,
    setAddressDetailsForAddLead,
    setAddressDetailsForAddLeadTemp,
    setDrawerOpen,
    setMultiTenantDrawerOpen,
  ])

  const handleKnockStatusModalClose = useCallback(
    (_hasExistingHistory?: boolean) => {
      setOpenKnockStatusModal(false)
      setShowHistory(false)
      setAddressDetailsForKnockStatus(null)
    },
    [setOpenKnockStatusModal, setShowHistory, setAddressDetailsForKnockStatus],
  )

  const onKnockStatusDeleteSuccess = useCallback(() => {
    setOpenKnockStatusModal(false)
    setShowHistory(false)
    setAddressDetailsForAddLeadTemp(null)
    setAddressDetailsForKnockStatus(null)
    // A KS-only pin (entityId 0) is removed from contactLocations by the forced
    // tile refresh, but the active-pin layer renders straight from clickedPin and
    // doesn't depend on the tile data — so the deleted badge lingers until React
    // state resets (browser/map refresh). Clear the active pin to drop it now.
    if (clickedPin?.entityId === 0) {
      setClickedPin(null)
      setHoveredPin(null)
    }
  }, [
    clickedPin,
    setClickedPin,
    setHoveredPin,
    setOpenKnockStatusModal,
    setShowHistory,
    setAddressDetailsForAddLeadTemp,
    setAddressDetailsForKnockStatus,
  ])

  const onSaveNewKnockStatusLocation = useCallback(
    (newKnockLocation: KnockStatusLocationBaseInput) => {
      // The server returns id at the root (KnockStatusLocationDto.id).
      // knockStatusLocationAddress.knockStatusLocationId is never populated in the response.
      const locationId =
        newKnockLocation.id ??
        newKnockLocation.knockStatusLocationAddress?.knockStatusLocationId ??
        undefined
      if (addressDetailsForAddLeadTemp)
        setAddressDetailsForAddLeadTemp({
          ...addressDetailsForAddLeadTemp,
          address: {
            ...addressDetailsForAddLeadTemp.address,
            knockStatusLocationId: locationId,
          },
          entityType: 'KnockStatus',
          knockStatusLocationId: locationId,
          knockStatusReason: newKnockLocation.customReason || '',
          knockStatusSettingName: newKnockLocation.knockStatusSettingName || '',
          knockStatusSettingsId: newKnockLocation.knockStatusSettingsId ?? null,
        })
      // This save (create or add-history) is the latest reason for the pin, so
      // drop any earlier inline-edit override that would otherwise mask it.
      setPendingCustomReason(null)
    },
    [
      addressDetailsForAddLeadTemp,
      setAddressDetailsForAddLeadTemp,
      setPendingCustomReason,
    ],
  )

  const handleUpdateAddressDetails = useCallback(
    (updatedItem: HistoryItem) => {
      const pinId = updatedItem?.knockStatusLocationId
      if (!pinId) return

      setPendingCustomReason({
        pinId,
        reason: updatedItem.customReason ?? null,
      })
    },
    [setPendingCustomReason],
  )

  useEffect(() => {
    async function handleSetMapPin(clickedLocation: EntityLocationDTO) {
      const { entityId, entityType, serviceLocationId, pinId } = clickedLocation

      // Determine pin variant (Lead, Customer, Mixed, etc.)
      let variant = entityTypeToMapPinVariant(entityType as ENTITY_TYPES)
      let selectedLocation: EntityLocationDTO | null = null

      // Try to find an exact match for this pinId among all contact locations
      let matchedLocations = contactLocations.filter(
        (item) => item.pinId === pinId,
      )

      if (matchedLocations.length > 0) {
        selectedLocation = { ...clickedLocation }
      }

      // If no exact match found, check for a mixed entity pin
      if (matchedLocations.length === 0) {
        matchedLocations = contactLocations.filter(
          (item: EntityLocationDTO) =>
            item.entities?.some((e) => {
              const isSameEntity = e.entityId === Number(entityId)
              const isSameLocation =
                e.entityType !== 'DraftLead'
                  ? e.serviceLocationId === Number(serviceLocationId)
                  : true // skip location check for DraftLead

              return isSameEntity && isSameLocation
            }) ?? false,
        )

        // If found, treat this pin as a mixed variant
        if (matchedLocations.length > 0) {
          variant = 'mixed'
          selectedLocation = { ...matchedLocations[0] }
        }
      }

      if (selectedLocation === null) {
        return
      }

      // Update state for selected map data
      setAddressDetailsForAddLeadTemp(null)
      setClickedPin(selectedLocation)

      if (
        selectedLocation.entityType?.toLowerCase() === MapPinTypeVariant.PestPac
      ) {
        setDrawerOpen(false)
        setMultiTenantDrawerOpen(false)
        return
      }

      // No backing Lead/Opportunity/DraftLead entity (e.g. a knock-status-only
      // pin) — close the drawer instead of firing a detail fetch that cannot
      // succeed (SH-1179).
      if (!hasResolvableEntity(selectedLocation)) {
        dispatch(clearEntityDetails())
        setDrawerOpen(false)
        setMultiTenantDrawerOpen(false)
        return
      }

      dispatch(clearPestPacLocationDetail())

      // === Handle different variants ===
      if (variant === 'mixed') {
        // For multi-tenant mixed locations
        dispatch(setSelectedServiceLocations(selectedLocation.entities))

        await dispatch(
          fetchMultiTenantEntityDetail(
            salesCenterContactLocationIds(selectedLocation.entities),
          ),
        )
        setMultiTenantDrawerOpen(true)
      }

      await dispatch(
        fetchEntityDetailAndProducts([
          entityTypeToMapPinVariant(entityType as ENTITY_TYPES),
          entityId,
          serviceLocationId ?? 0,
        ]),
      )
      dispatch(setSelectedServiceLocationId(Number(serviceLocationId)))
      setDrawerOpen(true)
    }

    if (!mapLinkClickedContactLocation) {
      clickedLinkPinIdRef.current = null
      return
    }

    if (
      clickedLinkPinIdRef.current !== mapLinkClickedContactLocation.pinId &&
      contactLocations.length > 0
    ) {
      clickedLinkPinIdRef.current = mapLinkClickedContactLocation.pinId
      handleSetMapPin(mapLinkClickedContactLocation)
    }
  }, [
    contactLocations,
    dispatch,
    entityTypeToMapPinVariant,
    mapLinkClickedContactLocation,
    setAddressDetailsForAddLeadTemp,
    setClickedPin,
    setDrawerOpen,
    setMultiTenantDrawerOpen,
  ])

  useEffect(() => {
    if (searchDrawerOpen) {
      closeAllDrawers()
    }
  }, [closeAllDrawers, searchDrawerOpen])

  // Opening the filter drawer should close the detail drawer first, mirroring
  // the search-drawer behaviour above, so the two never overlap (SH-1113).
  useEffect(() => {
    if (filterDrawerOpen) {
      closeAllDrawers()
    }
  }, [closeAllDrawers, filterDrawerOpen])

  useEffect(() => {
    if (!open) {
      setDrawerOpen(false)
      setMultiTenantDrawerOpen(false)
      setHoveredPin(null)
      setClickedPin(null)
      setFoundLocationFromSearch(false)
      dispatch(clearSelectedServiceLocation())
    }
  }, [
    dispatch,
    open,
    setClickedPin,
    setDrawerOpen,
    setFoundLocationFromSearch,
    setHoveredPin,
    setMultiTenantDrawerOpen,
  ])

  return {
    onMarkerHover,
    onMarkerClick,
    cancelHoverOpen,
    markPinClicked,
    isHoverPreview,
    handleMapClick,
    onClose,
    onActionClick,
    closeDrawerAndRefresh,
    closeAllDrawers,
    closeAndRefresh,
    resetMapState,
    handleAddressDetailsForAddLeadClose,
    handleLocationPreviewAddLead,
    handleAddLeadModalClose,
    handleAddLeadSaveSuccess,
    handleDuplicateManageClick,
    handleKnockStatusModalClose,
    handleLocationPreviewKnockStatus,
    handleLocationPreviewKnockStatusHistory,
    handleUpdateAddressDetails,
    viewClickHandler,
    viewMultiTenantClickHandler,
    onKnockStatusDeleteSuccess,
    onSaveNewKnockStatusLocation,
  }
}
